import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/AuthContext'
import {
  Leaf, Map, Users, Gift, Trophy, Settings, LogOut, Menu, X, Zap,
  Image as ImageIcon, Film, MessagesSquare, Radio, User, ChevronDown,
} from 'lucide-react'
import NotificationsBell from '@/components/NotificationsBell'
import Avatar from '@/components/Avatar'

const NAV_ITEMS = [
  { name: 'Dashboard',   icon: Leaf,   path: '/dashboard' },
  { name: 'Challenges',  icon: Trophy, path: '/challenges' },
  { name: 'Friends',     icon: Users,  path: '/friends' },
  { name: 'Messages',    icon: MessagesSquare, path: '/messages' },
  { name: 'Initiatives', icon: Map,    path: '/initiatives' },
  { name: 'Rewards',     icon: Gift,   path: '/rewards' },
  { name: 'Pricing',     icon: Zap,    path: '/pricing' },
  { name: 'Feed', icon: ImageIcon, path: '/feed' },
  { name: 'Flicks', icon: Film, path: '/flicks' },
  { name: 'Live', icon: Radio, path: '/live' },
]

export default function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const { profile, signOut, isAuthenticated } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const accountRef = useRef(null)

  const handleSignOut = async () => {
    setAccountOpen(false)
    await signOut()
    navigate('/auth')
  }

  const isActive = (path) => location.pathname === path

  // Close the account dropdown on outside click / Escape.
  useEffect(() => {
    if (!accountOpen) return
    const onClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false)
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') setAccountOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [accountOpen])

  const displayName = profile?.full_name || profile?.email?.split('@')[0] || 'My account'

  return (
    <div className="min-h-screen bg-[#f0f4f3] text-gray-800 flex flex-col">

      {/* Navbar */}
      <nav className="sticky top-0 z-50 px-4 py-4 bg-[#f0f4f3]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto clay-card px-5 py-3 flex items-center gap-3">

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 group shrink-0">
            <div className="bg-green-500 p-2 rounded-xl text-white shadow-md group-hover:rotate-12 transition-transform duration-300">
              <Leaf size={22} fill="currentColor" />
            </div>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-700 to-emerald-500">
              EarthTrackr
            </span>
          </Link>

          {/* Desktop nav links — icon-only on lg/xl, icon + label on 2xl */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center min-w-0">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                title={item.name}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-xl text-sm font-medium transition-all shrink-0 ${
                  isActive(item.path)
                    ? 'bg-white shadow-inner text-green-700 font-semibold'
                    : 'text-gray-600 hover:bg-white/50 hover:text-green-700'
                }`}
              >
                <item.icon size={16} className="shrink-0" />
                <span className="hidden 2xl:inline">{item.name}</span>
              </Link>
            ))}
          </div>

          {/* Desktop right side */}
          <div className="hidden lg:flex items-center gap-2 shrink-0 ml-auto lg:ml-0">
            {isAuthenticated ? (
              <>
                <NotificationsBell />

                {/* Account dropdown — profile / settings / sign out */}
                <div className="relative" ref={accountRef}>
                  <button
                    onClick={() => setAccountOpen((o) => !o)}
                    aria-haspopup="menu"
                    aria-expanded={accountOpen}
                    className="flex items-center gap-1 p-1 rounded-xl hover:bg-white/50 transition-all"
                    title="Account"
                  >
                    <Avatar profile={profile} size="w-8 h-8" textSize="text-xs" bordered />
                    <ChevronDown size={14} className="text-gray-400" />
                  </button>

                  {accountOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 animate-fade-in"
                    >
                      {/* Identity header */}
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="font-semibold text-sm text-gray-800 truncate">{displayName}</p>
                        {profile?.email && (
                          <p className="text-xs text-gray-400 truncate">{profile.email}</p>
                        )}
                        {profile?.is_pro && (
                          <span className="inline-block mt-1 text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            👑 PRO
                          </span>
                        )}
                      </div>

                      <Link
                        to="/profile"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <User size={16} className="text-gray-500" /> My Profile
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Settings size={16} className="text-gray-500" /> Settings
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                      >
                        <LogOut size={16} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/auth"
                className="clay-button px-5 py-2 rounded-2xl text-sm font-semibold"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden ml-auto p-2 text-gray-600 hover:bg-white/50 rounded-xl transition-all"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden mt-3 clay-card p-4 space-y-1 animate-fade-in">

            {/* Profile link at top */}
            {isAuthenticated && (
              <Link
                to="/profile"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl bg-green-50 mb-2"
              >
                <Avatar profile={profile} size="w-10 h-10" bordered />
                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    {profile?.full_name || profile?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-green-600">View profile</p>
                </div>
              </Link>
            )}

            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                  isActive(item.path)
                    ? 'bg-green-50 text-green-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon size={20} className="text-green-600" />
                {item.name}
              </Link>
            ))}

            {isAuthenticated && (
              <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 p-3 rounded-xl text-gray-600 hover:bg-gray-50"
                >
                  <Settings size={20} /> Settings
                </Link>
                <button
                  onClick={() => { setMenuOpen(false); handleSignOut() }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-red-500 hover:bg-red-50 text-left"
                >
                  <LogOut size={20} /> Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Page content */}
      <main className="flex-1 px-4 py-8 max-w-7xl w-full mx-auto">
        {children}
      </main>

      {/* Footer */}
      <footer className="py-8 text-sm text-gray-400">
        <div className="max-w-7xl mx-auto px-6 border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p>&copy; {new Date().getFullYear()} EarthTrackr. Make every choice count.</p>
          <div className="flex gap-6">
            <Link to="/pricing" className="hover:text-green-600 transition-colors">
              Pricing
            </Link>
            <Link to="/privacy" className="hover:text-green-600 transition-colors">
              Privacy Policy
            </Link>
            <a href="mailto:hello@earthtrackr.com" className="hover:text-green-600 transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
